import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiArrowLeft, 
  FiPlusCircle, 
  FiWifi,
  FiInfo
} from 'react-icons/fi';
import { addMacAddress, fetchApplications } from '../../services/macWhitelistService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';

const CreateMacAddress = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 获取URL参数中的应用ID
  const searchParams = new URLSearchParams(location.search);
  const applicationIdParam = searchParams.get('applicationId');
  
  // 状态
  const [macAddresses, setMacAddresses] = useState('');
  const [selectedAppId, setSelectedAppId] = useState(applicationIdParam || '');
  const [description, setDescription] = useState('');
  
  // 获取应用列表
  const { data: applications, isLoading: appsLoading } = useQuery(
    'applications',
    fetchApplications
  );
  
  // 添加MAC地址突变
  const addMutation = useMutation(
    (data) => addMacAddress(data.macAddresses, data.applicationId, data.description),
    {
      onSuccess: (data) => {
        const message = data.added > 0 
          ? `成功添加${data.added}个MAC地址${data.skipped > 0 ? `，跳过${data.skipped}个已存在的地址` : ''}`
          : data.message;
        toast.success(message);
        navigate('/mac-whitelist');
      },
      onError: (error) => {
        console.error('添加MAC地址错误:', error);
        toast.error(error.message || '添加MAC地址失败');
      }
    }
  );
  
  // 处理提交
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!macAddresses.trim()) {
      toast.error('请输入MAC地址');
      return;
    }
    
    if (!selectedAppId) {
      toast.error('请选择应用');
      return;
    }
    
    // 按行分割MAC地址，过滤空行
    const macList = macAddresses
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    if (macList.length === 0) {
      toast.error('请输入至少一个MAC地址');
      return;
    }
    
    addMutation.mutate({
      macAddresses: macList,
      applicationId: parseInt(selectedAppId, 10),
      description: description.trim()
    });
  };
  
  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="flex items-center mb-6">
        <Button
          onClick={() => navigate('/mac-whitelist')}
          variant="ghost"
          icon={<FiArrowLeft />}
          className="p-2 mr-4"
        />
        <div>
          <h1 className="text-2xl font-bold text-white">添加MAC地址</h1>
          <p className="text-gray-400 mt-1">将MAC地址添加到白名单，该MAC地址可通过verify-by-mac接口自动激活</p>
        </div>
      </motion.div>
      
      {/* 添加表单 */}
      <motion.div
        variants={itemVariants}
        className="bg-slate-800 rounded-xl shadow-lg p-6 mb-6"
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-6 space-y-5">
            {/* 应用选择 */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                选择应用 *
              </label>
              <select
                value={selectedAppId}
                onChange={(e) => setSelectedAppId(e.target.value)}
                className="w-full bg-slate-700 rounded-lg py-3 px-4 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={appsLoading}
              >
                <option value="">-- 选择应用 --</option>
                {applications?.map(app => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* MAC地址输入（支持批量） */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                MAC地址 *（支持批量添加，每行一个）
              </label>
              <textarea
                value={macAddresses}
                onChange={(e) => setMacAddresses(e.target.value)}
                placeholder="例如:&#10;00:1B:44:11:3A:B7&#10;001B44113AB7&#10;00-1B-44-11-3A-B7"
                rows={8}
                className="w-full bg-slate-700 rounded-lg py-3 px-4 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono resize-y"
                required
              />
              <p className="text-gray-400 text-xs mt-2">
                支持多种格式，每行一个MAC地址。系统会自动标准化（去除空格，转为小写）并去重。
              </p>
              {macAddresses && (
                <p className="text-blue-400 text-xs mt-1">
                  当前输入: {macAddresses.split('\n').filter(line => line.trim().length > 0).length} 个MAC地址
                </p>
              )}
            </div>
            
            {/* 描述输入 */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                描述（可选）
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="例如: 办公室PC-001"
                className="w-full bg-slate-700 rounded-lg py-3 px-4 text-white border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex flex-col gap-2 text-sm text-gray-400 mb-6">
            <div className="flex items-start">
              <FiInfo className="mr-2 mt-1" />
              <span>添加的MAC地址将自动加入白名单，使用verify-by-mac接口查询时会直接返回激活状态，无需手动激活授权码。</span>
            </div>
          </div>
          
          {/* 提交按钮 */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate('/mac-whitelist')}
              variant="secondary"
            >
              取消
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={<FiPlusCircle />}
              disabled={addMutation.isLoading || !selectedAppId || !macAddresses.trim()}
            >
              {addMutation.isLoading ? '添加中...' : '添加MAC地址'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateMacAddress;

